import React, { useState } from "react";
import { Link } from "react-router-dom";
import SeoMeta from "../SeoMeta";
import { FaUser, FaEnvelope, FaCommentDots, FaPaperPlane, FaWhatsapp, FaQuestionCircle, FaCheckCircle, FaHeadset } from "react-icons/fa";
import { useTheme } from "../Theme/ThemeContext";

export default function Contact() {
  const { currentTheme } = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: send message to backend
    setSubmitted(true);
  };

  const pageUrl = "https://www.code-arena.in/contact";
  const description =
    "Get in touch with CodeArena support team. Fill out the contact form or reach us via email, WhatsApp, or FAQs.";

  return (
    <>
      {/* ✅ SEO Meta */}
      <SeoMeta
        title="Contact Us | CodeArena"
        description={description}
        url={pageUrl}
        image="/logo192.png"
        keywords="CodeArena, Contact, Support, Jobs, Courses"
        type="website"
      />

      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800">
        {/* Hero Section */}
        <div className={`relative bg-gradient-to-r ${currentTheme.colors.gradient} py-20 overflow-hidden`}>
          {/* Decorative elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-10 w-64 h-64 bg-white/10 rounded-full filter blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-400/20 rounded-full filter blur-3xl"></div>
          </div>
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
              <FaHeadset className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-medium">Get in Touch</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Contact Us
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              We'd love to hear from you! Fill out the form below or reach us through the details provided.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-3xl mx-auto px-4 py-16 -mt-12 relative z-10">
          <div className="glass rounded-3xl shadow-xl border border-white/20 dark:border-white/10 p-8 sm:p-12">
            {/* Form */}
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaCheckCircle className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Thank You!</h2>
                <p className="text-gray-600 dark:text-gray-300">Your message has been sent. We'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2"
                  >
                    Your Name
                  </label>
                  <div className="relative">
                    <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700 focus:bg-white dark:focus:bg-neutral-600 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 transition-all outline-none text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500"
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700 focus:bg-white dark:focus:bg-neutral-600 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 transition-all outline-none text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2"
                  >
                    Message
                  </label>
                  <div className="relative">
                    <FaCommentDots className="absolute left-4 top-4 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
                    <textarea
                      name="message"
                      id="message"
                      rows="5"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700 focus:bg-white dark:focus:bg-neutral-600 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 transition-all outline-none text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500 resize-none"
                      placeholder="Write your message here..."
                    ></textarea>
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    className={`w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-semibold text-white bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 shadow-lg ${currentTheme.colors.glow} hover:shadow-xl transition-all duration-300 hover:scale-[1.02]`}
                  >
                    <FaPaperPlane className="w-4 h-4" />
                    Send Message
                  </button>
                </div>
              </form>
            )}

            {/* Support Section */}
            <div className="mt-12 pt-10 border-t border-neutral-100 dark:border-neutral-700 text-center">
              <h4 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">
                Need Immediate Help?
              </h4>
              <p className="text-neutral-600 dark:text-neutral-300 mb-8">
                Contact our support team and we'll get back to you as soon as possible.
              </p>

              <div className="flex justify-center gap-4 flex-wrap">
                <a
                  href="mailto:codearenaui@gmail.com"
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r ${currentTheme.colors.gradient} text-white font-medium shadow-md ${currentTheme.colors.glow} hover:shadow-lg transition-all duration-300 hover:scale-[1.02]`}
                >
                  <FaEnvelope className="w-4 h-4" />
                  Email Us
                </a>
                <a
                  href="https://wa.me/7057557851"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaWhatsapp className="w-4 h-4" />
                  WhatsApp Support
                </a>
                <Link
                  to="/faq"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl glass border-2 border-neutral-200 dark:border-neutral-600 text-neutral-700 dark:text-neutral-200 font-medium hover:border-accent-500 hover:text-accent-600 dark:hover:text-accent-400 transition-all duration-300"
                >
                  <FaQuestionCircle className="w-4 h-4" />
                  View FAQs
                </Link>
              </div>
            </div>

            {/* Back to Home */}
            <div className="text-center mt-10">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 text-sm font-medium transition-colors"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
